import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useCurrentUser, type CurrentUser } from "@/lib/auth";
import { aclRuleForPath, isAllowed, isAppUser, normalizeAclPath } from "@/lib/acl";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";

function groupsForAppAccess(user: CurrentUser): string[] {
  const real = user.groups_real;
  if (Array.isArray(real) && real.length > 0) return real;
  return user.groups ?? [];
}

export interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const location = useLocation();
  const { user, loading, error } = useCurrentUser();

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-label-secondary">
        <div className="flex items-center gap-2 text-sm">
          <Spinner />
          <span>Sitzung wird geprüft …</span>
        </div>
      </div>
    );
  }

  if (!user && !error) {
    return <Navigate to="/login" replace />;
  }

  if (error || !user) {
    return (
      <div className="mx-auto max-w-md py-10">
        <Alert variant="error" title="Sitzung konnte nicht geladen werden">
          {error || "Bitte erneut anmelden."}
        </Alert>
      </div>
    );
  }

  if (!isAppUser(groupsForAppAccess(user))) {
    return <Navigate to="/kein-zugang" replace />;
  }

  const rule = aclRuleForPath(normalizeAclPath(location.pathname));
  if (rule && !isAllowed(user.groups ?? [], rule)) {
    return (
      <div className="mx-auto max-w-lg py-10">
        <Alert variant="warning" title="Kein Zugriff auf diesen Bereich">
          Ihr Konto ist angemeldet, dieser Bereich ist dafür aber nicht freigegeben. Auswertungen,
          Einstellungen und andere buchhaltungsrelevante Daten stehen der Gruppe Büro sowie den
          dafür vorgesehenen Rollen zur Verfügung.
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}
